import React, { useEffect, useState } from "react";
import { Check } from "lucide-react";
import { useI18n } from "../../i18n";
import { useAuth } from "../../context/AuthContext";
import { api, apiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";

export default function Subscription() {
  const { t, lang, fmt } = useI18n();
  const { org, refreshOrg, user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [cycle, setCycle] = useState("monthly");
  const isAdmin = user.role === "client_admin";
  const [bank, setBank] = useState(null);
  const [paying, setPaying] = useState(false);

  const load = () => {
    api.get("/plans").then((r) => setPlans(r.data.plans));
    api.get("/invoices").then((r) => setInvoices(r.data)).catch(() => {});
  };
  useEffect(load, []);

  useEffect(() => {
    const sid = new URLSearchParams(window.location.search).get("session_id");
    if (!sid) return;
    let tries = 0;
    const poll = async () => {
      tries++;
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") { toast.success(t("status_active")); await refreshOrg(); load(); window.history.replaceState({}, "", "/app/subscription"); return; }
      } catch (e) { /* keep polling */ }
      if (tries < 8) setTimeout(poll, 2000); else window.history.replaceState({}, "", "/app/subscription");
    };
    poll();
  }, []);

  const payCard = async (plan_id) => {
    setPaying(true);
    try {
      const { data } = await api.post("/payments/checkout", { plan_id, billing_cycle: cycle, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiError(e)); setPaying(false); }
  };

  const payBank = async (plan_id) => {
    try {
      const { data } = await api.post("/payments/bank-transfer", { plan_id, billing_cycle: cycle });
      setBank(data.bank); await refreshOrg(); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const current = plans.find((p) => p.id === org?.plan_id);

  return (
    <div className="space-y-6 gc-fade-up">
      <h1 className="font-display text-2xl font-bold text-[hsl(217_63%_15%)]">{t("sub_title")}</h1>

      <div className="rounded-2xl border border-border bg-card p-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs text-muted-foreground">{t("your_plan")}</div>
            <div className="mt-1 font-display text-xl font-bold text-[hsl(217_63%_15%)]">{current ? (lang === "ar" ? current.name_ar : current.name_en) : t("no_plan")}</div>
          </div>
          <div className="flex gap-6">
            <div><div className="text-xs text-muted-foreground">{t("status")}</div><div className="mt-1 font-semibold text-[hsl(158_64%_28%)]">{org?.subscription_status === "active" ? t("status_active") : t("status_trial")}</div></div>
            <div><div className="text-xs text-muted-foreground">{t("advisory_credits")}</div><div className="mt-1 font-mono font-bold">{org?.advisory_credits ?? 0}</div></div>
          </div>
        </div>
      </div>

      {isAdmin && (
        <>
          <div className="flex items-center gap-2">
            <button data-testid="cycle-monthly" onClick={() => setCycle("monthly")} className={`rounded-full px-4 py-1.5 text-sm ${cycle === "monthly" ? "bg-[hsl(217_63%_15%)] text-white" : "border border-border"}`}>{t("monthly")}</button>
            <button data-testid="cycle-annual" onClick={() => setCycle("annual")} className={`rounded-full px-4 py-1.5 text-sm ${cycle === "annual" ? "bg-[hsl(217_63%_15%)] text-white" : "border border-border"}`}>{t("annual")}</button>
          </div>
          <div className="grid lg:grid-cols-4 md:grid-cols-2 gap-4">
            {plans.map((p) => {
              const price = cycle === "annual" ? p.price_annual : p.price_monthly;
              const isCurrent = org?.plan_id === p.id;
              return (
                <div key={p.id} className={`rounded-2xl border bg-card p-5 flex flex-col ${p.highlight ? "border-[hsl(158_64%_28%)]" : "border-border"}`}>
                  <div className="font-display font-bold text-[hsl(217_63%_15%)]">{lang === "ar" ? p.name_ar : p.name_en}</div>
                  <div className="mt-3 font-mono text-2xl font-bold">{price > 0 ? `${fmt(price)}` : t("custom")}<span className="text-xs text-muted-foreground ms-1">{price > 0 ? t("sar") : ""}</span></div>
                  <ul className="mt-4 space-y-2 flex-1 text-xs">
                    {(lang === "ar" ? p.features_ar : p.features_en).slice(0, 5).map((x, i) => (
                      <li key={i} className="flex gap-1.5"><Check className="h-3.5 w-3.5 text-[hsl(158_64%_28%)] mt-0.5 shrink-0" />{x}</li>
                    ))}
                  </ul>
                  <div className="mt-4 space-y-2">
                    <Button data-testid={`pay-card-${p.id}`} disabled={isCurrent || p.price_monthly === 0 || paying} onClick={() => payCard(p.id)}
                      className={`rounded-full w-full ${isCurrent ? "bg-secondary text-foreground" : "bg-[hsl(158_64%_28%)] hover:bg-[hsl(158_64%_24%)]"}`}>
                      {isCurrent ? t("current_plan") : p.price_monthly === 0 ? t("contact_sales") : t("pay_card")}
                    </Button>
                    {!isCurrent && p.price_monthly > 0 && (
                      <Button data-testid={`pay-bank-${p.id}`} variant="outline" onClick={() => payBank(p.id)}
                        className="rounded-full w-full border-[hsl(217_63%_15%)] text-[hsl(217_63%_15%)] hover:bg-secondary">
                        {t("pay_bank")}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      <div className="rounded-2xl border border-border bg-card p-6">
        <div className="font-semibold text-[hsl(217_63%_15%)] mb-4">{t("billing_history")}</div>
        <table className="w-full text-sm">
          <thead className="text-muted-foreground text-xs"><tr>
            <th className="text-start py-2">{t("created")}</th><th className="text-start py-2">{t("your_plan")}</th>
            <th className="text-start py-2">{t("amount")}</th><th className="text-start py-2">{t("cycle")}</th><th className="text-start py-2">{t("status")}</th>
          </tr></thead>
          <tbody>
            {invoices.length === 0 && <tr><td colSpan={5} className="py-6 text-center text-muted-foreground">—</td></tr>}
            {invoices.map((inv) => (
              <tr key={inv.id} className="border-t border-border">
                <td className="py-2 font-mono text-xs">{inv.created_at?.slice(0, 10)}</td>
                <td className="py-2">{inv.plan_id}</td>
                <td className="py-2 font-mono">{fmt(inv.amount)} {t("sar")}</td>
                <td className="py-2">{inv.cycle === "annual" ? t("annual") : t("monthly")}</td>
                <td className="py-2"><span className={`text-xs font-medium ${inv.status === "paid" ? "text-[hsl(158_64%_28%)]" : "text-[hsl(38_92%_45%)]"}`}>{inv.status === "paid" ? t("paid") : inv.status}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {bank && (
        <div data-testid="bank-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setBank(null)}>
          <div className="bg-card rounded-2xl border border-border p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="font-display font-bold text-lg text-[hsl(217_63%_15%)] mb-4">{t("pay_bank")}</div>
            <div className="space-y-2.5 text-sm">
              <div className="flex justify-between gap-4"><span className="text-muted-foreground">{t("bank_beneficiary")}</span><b className="text-end">{bank.beneficiary}</b></div>
              <div className="flex justify-between gap-4"><span className="text-muted-foreground">{t("bank_name")}</span><b>{bank.bank_name}</b></div>
              <div className="flex justify-between gap-4"><span className="text-muted-foreground">IBAN</span><b className="font-mono text-xs">{bank.iban}</b></div>
              <div className="flex justify-between gap-4"><span className="text-muted-foreground">{t("amount")}</span><b>{fmt(bank.amount)} {t("sar")}</b></div>
              <div className="flex justify-between gap-4"><span className="text-muted-foreground">{t("bank_ref")}</span><b>{bank.reference}</b></div>
            </div>
            <p className="text-xs text-muted-foreground mt-4 leading-relaxed">{t("bank_note")}</p>
            <Button data-testid="bank-close" onClick={() => setBank(null)} className="mt-4 rounded-full w-full bg-[hsl(217_63%_15%)] hover:bg-[hsl(217_63%_20%)]">{t("bank_done")}</Button>
          </div>
        </div>
      )}
    </div>
  );
}
